import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import mediaUploadService, { PENDING_UPLOAD_KEY } from '../../services/mediaUploadService';

const Deliveries = ({ embedded = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    files: {},
    overall: { percentage: 0, completedFiles: 0, totalFiles: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const fileInputRef = useRef(null);
  const uploadingRef = useRef(false);

  // Keep ref in sync so visibilitychange can read current upload state
  useEffect(() => { uploadingRef.current = uploading; }, [uploading]);

  // Check for an interrupted upload session on mount + when returning from background
  useEffect(() => {
    const checkPending = () => {
      try {
        const raw = localStorage.getItem(PENDING_UPLOAD_KEY);
        if (raw) setPendingSession(JSON.parse(raw));
        else setPendingSession(null);
      } catch {}
    };
    checkPending();
    document.addEventListener('visibilitychange', checkPending);
    return () => document.removeEventListener('visibilitychange', checkPending);
  }, []);

  // Load completed bookings that need delivery
  useEffect(() => {
    if (!user) return;
    loadBookings();
  }, [user]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_date,
          booking_time,
          photography_type,
          city,
          status,
          delivery_status,
          final_price_sar,
          customer_id,
          customer:customer_id (
            full_name,
            avatar_url
          )
        `)
        .eq('assigned_photographer_id', user.id)
        .in('status', ['awaiting_delivery', 'completed'])
        .order('booking_date', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setToast({ type: 'error', message: 'فشل تحميل الحجوزات' });
    } finally {
      setLoading(false);
    }
  };

  const loadDelivery = async (bookingId) => {
    try {
      const { data: existingDelivery, error: deliveryError } = await supabase
        .from('booking_deliveries')
        .select('*')
        .eq('booking_id', bookingId)
        .single();

      if (deliveryError && deliveryError.code !== 'PGRST116') {
        throw deliveryError;
      }

      if (existingDelivery) {
        setDelivery(existingDelivery);
        const { data: deliveryFiles, error: filesError } = await supabase
          .from('delivery_files')
          .select('*')
          .eq('delivery_id', existingDelivery.id)
          .order('sort_order', { ascending: true });

        if (filesError) throw filesError;
        setFiles(deliveryFiles || []);
      } else {
        setDelivery(null);
        setFiles([]);
      }
    } catch (error) {
      console.error('Error loading delivery:', error);
      setToast({ type: 'error', message: 'فشل تحميل بيانات التسليم' });
    }
  };

  const handleSelectBooking = async (booking) => {
    setSelectedBooking(booking);
    await loadDelivery(booking.id);
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    if (selectedBooking?.status !== 'awaiting_delivery') {
      setToast({ type: 'error', message: 'لا يمكن رفع الملفات إلا بعد إنهاء الجلسة الميدانية' });
      if (event.target) event.target.value = '';
      return;
    }

    // Validate all files first
    const validationResults = selectedFiles.map(f => ({
      file: f,
      ...mediaUploadService.validateFile(f)
    }));

    const invalidFiles = validationResults.filter(r => !r.valid);
    if (invalidFiles.length > 0) {
      setToast({ 
        type: 'error', 
        message: invalidFiles.map(f => f.errors[0]).join('\n')
      });
      // Filter out invalid files
      const validFiles = validationResults.filter(r => r.valid).map(r => r.file);
      if (validFiles.length === 0) return;
    }

    const validFiles = validationResults.filter(r => r.valid).map(r => r.file);
    setUploading(true);
    setUploadProgress({
      files: {},
      overall: { percentage: 0, completedFiles: 0, totalFiles: validFiles.length }
    });

    try {
      // Create or get delivery record
      let currentDelivery = delivery;
      if (!currentDelivery) {
        const { data: newDelivery, error: createError } = await supabase
          .from('booking_deliveries')
          .insert({
            booking_id: selectedBooking.id,
            photographer_id: user.id,
            customer_id: selectedBooking.customer_id,
            status: 'uploading'
          })
          .select()
          .single();

        if (createError) throw createError;
        currentDelivery = newDelivery;
        setDelivery(newDelivery);
      } else {
        // Update status to uploading
        await supabase
          .from('booking_deliveries')
          .update({ status: 'uploading' })
          .eq('id', currentDelivery.id);
      }

      // Upload files using the new service
      const results = await mediaUploadService.uploadMultipleFiles(
        validFiles,
        user.id,
        selectedBooking.id,
        // Per-file progress
        (index, fileName, progress) => {
          setUploadProgress(prev => ({
            ...prev,
            files: {
              ...prev.files,
              [fileName]: progress
            }
          }));
        },
        // Overall progress
        (overall) => {
          setUploadProgress(prev => ({
            ...prev,
            overall
          }));
        }
      );

      // Process successful uploads
      const successfulUploads = results.filter(r => r.success);
      const failedUploads = results.filter(r => !r.success);

      if (successfulUploads.length > 0) {
        // Generate thumbnails for videos and images
        const fileRecords = [];
        for (const upload of successfulUploads) {
          let thumbnailPath = null;
          
          try {
            const originalFile = validFiles.find(f => f.name === upload.fileName);
              if (originalFile) {
              let thumbnailBlob = null;

              // تخطي thumbnail الفيديوهات الكبيرة > 100MB لتجنب crash الذاكرة على الجوال
              const VIDEO_THUMB_SKIP_SIZE = 100 * 1024 * 1024;
              if (upload.fileType === 'video' && originalFile.size <= VIDEO_THUMB_SKIP_SIZE) {
                thumbnailBlob = await mediaUploadService.generateVideoThumbnail(originalFile);
              } else if (upload.fileType === 'image') {
                thumbnailBlob = await mediaUploadService.generateImageThumbnail(originalFile);
              }

              if (thumbnailBlob) {
                const thumbPath = upload.storagePath.replace(/\.[^.]+$/, '_thumb.jpg');
                const { error: thumbError } = await supabase.storage
                  .from('deliveries')
                  .upload(thumbPath, thumbnailBlob, { upsert: true });

                if (!thumbError) {
                  thumbnailPath = thumbPath;
                }
              }
            }
          } catch (e) {
            console.warn('Failed to generate thumbnail:', e);
          }

          fileRecords.push({
            delivery_id: currentDelivery.id,
            file_name: upload.fileName,
            file_path: upload.storagePath,
            file_type: upload.fileType,
            file_size_bytes: upload.fileSize,
            mime_type: upload.mimeType,
            thumbnail_path: thumbnailPath,
            sort_order: files.length + fileRecords.length
          });
        }

        // Insert file records
        const { error: insertError } = await supabase
          .from('delivery_files')
          .insert(fileRecords);

        if (insertError) throw insertError;

        // Update delivery counts
        const allFiles = [...files, ...fileRecords];
        const totalPhotos = allFiles.filter(f => f.file_type === 'image').length;
        const totalVideos = allFiles.filter(f => f.file_type === 'video').length;
        const totalSize = allFiles.reduce((sum, f) => sum + (f.file_size_bytes || 0), 0);

        await supabase
          .from('booking_deliveries')
          .update({
            total_photos: totalPhotos,
            total_videos: totalVideos,
            total_size_bytes: totalSize,
            status: 'pending',
            updated_at: new Date().toISOString()
          })
          .eq('id', currentDelivery.id);

        setToast({ 
          type: 'success', 
          message: `تم رفع ${successfulUploads.length} ملفات بنجاح${failedUploads.length > 0 ? ` (فشل ${failedUploads.length})` : ''}`
        });
        await loadDelivery(selectedBooking.id);
      }

      if (failedUploads.length > 0 && successfulUploads.length === 0) {
        setToast({ type: 'error', message: 'فشل رفع جميع الملفات' });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setToast({ type: 'error', message: 'فشل رفع الملفات: ' + error.message });
    } finally {
      setUploading(false);
      setUploadProgress({
        files: {},
        overall: { percentage: 0, completedFiles: 0, totalFiles: 0 }
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الملف؟')) return;

    try {
      // Delete from storage
      await supabase.storage.from('deliveries').remove([file.file_path]);
      if (file.thumbnail_path) {
        await supabase.storage.from('deliveries').remove([file.thumbnail_path]);
      }

      // Delete record
      await supabase.from('delivery_files').delete().eq('id', file.id);

      // Update counts
      const newFiles = files.filter(f => f.id !== file.id);
      const totalPhotos = newFiles.filter(f => f.file_type === 'image').length;
      const totalVideos = newFiles.filter(f => f.file_type === 'video').length;
      const totalSize = newFiles.reduce((sum, f) => sum + (f.file_size_bytes || 0), 0);

      await supabase
        .from('booking_deliveries')
        .update({
          total_photos: totalPhotos,
          total_videos: totalVideos,
          total_size_bytes: totalSize,
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      setFiles(newFiles);
      setToast({ type: 'success', message: 'تم حذف الملف' });
    } catch (error) {
      console.error('Error deleting file:', error);
      setToast({ type: 'error', message: 'فشل حذف الملف' });
    }
  };

  const handleDeliverToCustomer = async () => {
    if (selectedBooking?.status !== 'awaiting_delivery') {
      setToast({ type: 'error', message: 'لا يمكن التسليم إلا بعد إنهاء الجلسة الميدانية' });
      return;
    }
    if (!delivery || files.length === 0) {
      setToast({ type: 'error', message: 'يجب رفع ملفات أولاً' });
      return;
    }

    try {
      const { error: deliveryError } = await supabase
        .from('booking_deliveries')
        .update({
          status: 'delivered',
          delivered_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);
      if (deliveryError) throw deliveryError;

      const { data: updatedRows, error: bookingError } = await supabase
        .from('bookings')
        .update({
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedBooking.id)
        .select('id');
      if (bookingError) throw bookingError;
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error('لا صلاحية لتحديث حالة الحجز — تأكد من تطبيق إصلاح قاعدة البيانات');
      }

      // 🔔 إشعار للعميل: الملفات جاهزة للتحميل
      try {
        const { notifyCustomerFilesDelivered } = await import('../../services/notificationService');
        notifyCustomerFilesDelivered(selectedBooking.customerId || selectedBooking.customer_id, selectedBooking.id);
      } catch (_) {}

      setToast({ type: 'success', message: 'تم تسليم الملفات للعميل بنجاح! ✅' });
      setDelivery({ ...delivery, status: 'delivered', delivered_at: new Date().toISOString() });
      loadBookings();
    } catch (error) {
      console.error('Error delivering:', error);
      setToast({ type: 'error', message: error.message || 'فشل تسليم الملفات' });
    }
  };

  const getFileUrl = async (filePath) => {
    return await mediaUploadService.getSignedUrl(filePath);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'في الانتظار', color: 'bg-yellow-100 text-yellow-800' },
      uploading: { label: 'جاري الرفع', color: 'bg-blue-100 text-blue-800' },
      delivered: { label: 'تم التسليم', color: 'bg-green-100 text-green-800' },
      downloaded: { label: 'تم التحميل', color: 'bg-blue-100 text-blue-800' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'py-2' : 'container mx-auto px-4 py-6 max-w-6xl'} dir="rtl">

      {/* Page header */}
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-900">تسليم الملفات</h1>
          <p className="text-sm text-slate-400 mt-1">ارفع الصور والفيديوهات وسلّمها للعميل</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Column 1: Bookings list ── */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-700">الحجوزات</p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {bookings.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <Icon name="CalendarX2" size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">لا توجد حجوزات</p>
                </div>
              ) : (
                bookings.map((booking) => {
                  const isSelected = selectedBooking?.id === booking.id;
                  return (
                    <button
                      key={booking.id}
                      onClick={() => handleSelectBooking(booking)}
                      className={`w-full px-4 py-3 text-right transition-colors ${
                        isSelected
                          ? 'bg-primary/5 border-r-2 border-primary'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {booking.customer?.avatar_url
                            ? <Image src={booking.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                            : <Icon name="User" size={16} className="text-slate-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-slate-800'}`}>
                            {booking.customer?.full_name || 'عميل'}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatDate(booking.booking_date)}</p>
                        </div>
                        {booking.delivery_status === 'delivered' && (
                          <Icon name="CheckCircle2" size={15} className="text-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Column 2: Upload panel ── */}
        <div className="lg:col-span-2">
          {!selectedBooking ? (
            <div className="bg-white rounded-2xl border border-slate-200 flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <Icon name="FolderOpen" size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">اختر حجزاً من القائمة</p>
              <p className="text-sm text-slate-400 mt-1">لبدء رفع وتسليم الملفات</p>
            </div>
          ) : (
            <div className="space-y-4">

              {/* Booking header bar */}
              <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {selectedBooking.customer?.avatar_url
                        ? <Image src={selectedBooking.customer.avatar_url} alt="" className="w-full h-full object-cover" />
                        : <Icon name="User" size={18} className="text-slate-400" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {selectedBooking.customer?.full_name || 'عميل'}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDate(selectedBooking.booking_date)}
                        {selectedBooking.city ? ` · ${selectedBooking.city}` : ''}
                        {selectedBooking.photography_type ? ` · ${selectedBooking.photography_type}` : ''}
                      </p>
                    </div>
                  </div>
                  {delivery?.status && getStatusBadge(delivery.status)}
                </div>
              </div>

              {/* Interrupted session notice */}
              {selectedBooking.status === 'awaiting_delivery' && pendingSession && pendingSession.bookingId === selectedBooking?.id && !uploading && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <Icon name="RotateCcw" size={16} className="text-amber-600 flex-shrink-0" />
                  <p className="flex-1 text-sm text-amber-700">
                    رفع متوقف لـ <span className="font-semibold">{pendingSession.files.length}</span> ملف — اختر نفس الملفات للاستئناف
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    استئناف
                  </button>
                  <button
                    onClick={() => { localStorage.removeItem(PENDING_UPLOAD_KEY); setPendingSession(null); }}
                    className="text-amber-400 hover:text-amber-600"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}

              {/* Upload zone — only after the field session is completed */}
              {selectedBooking.status === 'awaiting_delivery' && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                />

                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`rounded-xl border-2 border-dashed transition-colors ${
                    uploading
                      ? 'border-slate-200 bg-slate-50 cursor-default p-6'
                      : 'border-slate-300 hover:border-primary hover:bg-primary/5 cursor-pointer p-8 text-center'
                  }`}
                >
                  {uploading ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-slate-700">
                              {uploadProgress.overall.completedFiles} / {uploadProgress.overall.totalFiles} ملف
                            </span>
                            <span className="text-sm font-semibold text-primary">
                              {uploadProgress.overall.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.overall.percentage}%` }}
                            />
                          </div>
                          {(() => {
                            const active = Object.entries(uploadProgress.files).find(
                              ([, p]) => p.status !== 'completed' && p.status !== 'failed'
                            );
                            return active ? (
                              <p className="text-xs text-slate-400 truncate mt-1.5">{active[0]}</p>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Icon name="Upload" size={22} className="text-primary" />
                      </div>
                      <p className="text-slate-700 font-medium">اضغط لاختيار الملفات</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        صور وفيديوهات · حتى 5GB للملف · JPEG, PNG, HEIC, MP4, MOV
                      </p>
                    </>
                  )}
                </div>
              </div>
              )}

              {/* Files grid */}
              {files.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  {/* Stats row */}
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-semibold text-slate-700">
                      الملفات المرفوعة
                      <span className="text-slate-400 font-normal mr-1">({files.length})</span>
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      {(delivery?.total_photos || 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <Icon name="Image" size={12} />
                          {delivery.total_photos}
                        </span>
                      )}
                      {(delivery?.total_videos || 0) > 0 && (
                        <span className="flex items-center gap-1">
                          <Icon name="Film" size={12} />
                          {delivery.total_videos}
                        </span>
                      )}
                      {delivery?.total_size_bytes > 0 && (
                        <span>{formatFileSize(delivery.total_size_bytes)}</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
                    {files.map((file) => (
                      <FileCard
                        key={file.id}
                        file={file}
                        getFileUrl={getFileUrl}
                        onDelete={() => handleDeleteFile(file)}
                        canDelete={selectedBooking.status === 'awaiting_delivery' && delivery?.status !== 'delivered'}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Deliver action */}
              {selectedBooking.status === 'awaiting_delivery' && files.length > 0 && delivery?.status !== 'delivered' && (
                <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">جاهز للتسليم؟</p>
                    <p className="text-xs text-slate-400 mt-0.5">سيصل إشعار للعميل فور التسليم</p>
                  </div>
                  <Button
                    onClick={handleDeliverToCustomer}
                    className="bg-button hover:opacity-90 text-button-foreground px-5 py-2.5 text-sm flex-shrink-0"
                    iconName="Send"
                    iconPosition="right"
                  >
                    تسليم للعميل
                  </Button>
                </div>
              )}

              {/* Delivered state */}
              {delivery?.status === 'delivered' && (
                <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle2" size={18} className="text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">تم التسليم بنجاح</p>
                    <p className="text-xs text-slate-400">{formatDate(delivery.delivered_at)}</p>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

// File Card Component
const FileCard = ({ file, getFileUrl, onDelete, canDelete }) => {
  const [url, setUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUrls = async () => {
      if (file.file_type === 'image') {
        // للصور: نستخدم Supabase Storage Transform مباشرة — لا حاجة لملف thumbnail منفصل
        const thumb = await mediaUploadService.getImageThumbnailUrl(file.file_path);
        setThumbnailUrl(thumb);
        setUrl(thumb); // نفس الـ URL مع transform للعرض، والـ URL الكامل يُفتح عند الضغط
      } else {
        // للفيديو: نحمّل الـ thumbnail المخزّن إن وُجد
        if (file.thumbnail_path) {
          const thumbUrl = await getFileUrl(file.thumbnail_path);
          setThumbnailUrl(thumbUrl);
        }
      }
      const mainUrl = await getFileUrl(file.file_path);
      setUrl(mainUrl);
      setLoading(false);
    };
    loadUrls();
  }, [file.file_path, file.thumbnail_path, file.file_type]);

  const displayUrl = file.file_type === 'video' && thumbnailUrl ? thumbnailUrl : url;

  return (
    <div className="relative group rounded-xl overflow-hidden bg-slate-100 aspect-square">
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : file.file_type === 'video' ? (
        <div className="absolute inset-0">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={file.file_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-800 flex items-center justify-center">
              <Icon name="Film" size={24} className="text-slate-500" />
            </div>
          )}
          {/* Play overlay always visible for videos */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center">
              <Icon name="Play" size={16} className="text-white ml-0.5" />
            </div>
          </div>
        </div>
      ) : (
        <img src={url} alt={file.file_name} className="w-full h-full object-cover" />
      )}

      {/* Hover actions */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
            <Icon name="Eye" size={14} className="text-white" />
          </a>
        )}
        {canDelete && (
          <button onClick={onDelete}
            className="w-8 h-8 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors">
            <Icon name="Trash2" size={14} className="text-white" />
          </button>
        )}
      </div>

      {/* Size label */}
      <div className="absolute bottom-1.5 left-1.5">
        <span className="text-[10px] font-medium bg-black/50 text-white px-1.5 py-0.5 rounded">
          {formatFileSizeShort(file.file_size_bytes)}
        </span>
      </div>
    </div>
  );
};

const formatFileSizeShort = (bytes) => {
  if (!bytes) return '0B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i)) + sizes[i];
};

export default Deliveries;
